import { Box } from '@material-ui/core';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { isArray, uniqBy } from 'lodash';
import { FormApi } from 'final-form';
import { FieldSelect, FieldTextInput, InlineTextButton, TypographyWrapper } from '../../components';
import { useShopConfig, useShopConfigV2 } from '../../hooks/shopConfig';
import { defaultTreetStyles } from '../../shopConfig/config';
import { getFilterOptions } from '../../shopConfig/configHelper';
import { FilterId } from '../../types/filters/filters';
import { ShopifyProductVariantEdge } from '../../types/shopify/product';
import { ProductOption } from '../../types/shopify/productOption';
import {
  filterDefaultShopifyOptions,
  formatVariantOptionForDisplay,
} from '../../util/shopifyHelpers';
import { required } from '../../util/validators';
import Heading from './Heading';
import { findMatchingProductVariant } from './EditListingPage.utils';
import { useListingFlowConfig } from '../../hooks/useListingFlowConfig';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import { useLazyApi } from '../../hooks/useLazyApi';
import { useOwnListingVersions } from './hooks/useOwnListingVersions';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import { getProductVariantQuery } from '../../components/Shopify/Shopify.utils';
import { TypographyFormat } from '../../components/TypographyWrapper/TypographyWrapper';
import {
  fetchShopifyVariantByQuery,
  fetchVariantById,
  fetchVariantsByProductId,
} from '../../util/api';
import AppContext from '../../context/AppContext';
import { ListingFlowFormField } from './ListingFlowTypes';
import { FindItemMethod } from '../../types/sharetribe/listing';

export const UNKNOWN_CATEGORIES = ['N/A', 'UNKNOWN'];

export type CustomItemDetailsInputField = {
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  shouldDisplay: boolean;
};

type Option = {
  key: string;
  label: string;
  addTagIfSelected?: boolean;
};

interface ManualEntryButtonProps {
  isManualEntryToggled: boolean;
  toggleManualEntry: () => void;
}

const handleCustomVariantOptionChange = (
  form: FormApi<Record<string, any>, Partial<Record<string, any>>>,
  customVariantsToShow: CustomVariantDropDownOption[]
) => {
  const { values } = form.getState();
  const { tags = [] } = values;
  let updatedTags;
  // fix here
  customVariantsToShow.forEach((customVariant: CustomVariantDropDownOption) => {
    if (!customVariant?.key || !isArray(customVariant.values)) return;
    const addTagForSelectedValues = customVariant.values.filter(
      ({ addTagIfSelected }) => addTagIfSelected
    );
    if (isEmpty(addTagForSelectedValues)) return;
    const isSelected = addTagForSelectedValues.find(
      (selectedValue) => selectedValue.key === values[customVariant.key as keyof typeof values]
    );
    if (isSelected && !tags.includes(customVariant.key)) {
      updatedTags = [...tags, customVariant.key];
    } else {
      updatedTags = tags.filter((tag: string) => tag !== customVariant.key);
    }
  });

  if (updatedTags) {
    form.change(ListingFlowFormField.Tags, updatedTags);
  }
};

const ManualEntryButton: FC<ManualEntryButtonProps> = (props) => {
  const { isManualEntryToggled, toggleManualEntry } = props;
  const text = isManualEntryToggled ? 'Select from options' : `Don’t see your option?`;

  return (
    <InlineTextButton onClick={toggleManualEntry}>
      <TypographyWrapper
        variant="body2"
        component="span"
        format={TypographyFormat.Underlined}
        typographyOverrides={{
          style: {
            color: defaultTreetStyles.gray60,
            fontSize: '12px',
          },
        }}
      >
        {text}
      </TypographyWrapper>
    </InlineTextButton>
  );
};

interface VariantOptionsDropdownProps {
  name: string;
  options: Option[];
  shouldIncludeOtherOption?: boolean;
  onChange?: () => void;
  isRequired?: boolean;
  description?: string;
}

const VariantOptionsDropdown: FC<VariantOptionsDropdownProps> = (props) => {
  const {
    name,
    options,
    shouldIncludeOtherOption,
    onChange,
    isRequired = true,
    description,
  } = props;
  const formattedName = formatVariantOptionForDisplay(name);

  return (
    <Box pt={1}>
      <FieldSelect
        name={name}
        id={name}
        label={formattedName}
        key={name}
        validate={isRequired ? required(`Please choose an option.`) : undefined}
        description={description}
      >
        <option disabled value="">
          Select an option
        </option>
        {options.map(({ key, label }) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
        {shouldIncludeOtherOption && <option value="Other">Other</option>}
      </FieldSelect>
      {!!onChange && <OnChange name={name}>{onChange}</OnChange>}
    </Box>
  );
};

interface VariantOptionsTextInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  type?: string;
  isRequired?: boolean;
  onChange?: () => void;
}

export interface CustomVariantDropDownOption {
  key?: string;
  name: string;
  values: Option[];
  description?: string;
  showForShopifyProduct?: boolean;
}

const VariantOptionsTextInput: FC<VariantOptionsTextInputProps> = (props) => {
  const { name, label, placeholder, type, isRequired, onChange } = props;

  const handleChange = onChange || (() => {});
  const debouncedChange = useCallback(debounce(handleChange, 1000), []);

  return (
    <Box pt={1}>
      <FieldTextInput
        key={name}
        id={name}
        name={name}
        label={label || formatVariantOptionForDisplay(name)}
        type={type || 'text'}
        placeholder={placeholder || 'Enter a value here'}
        validate={isRequired ? required('Please enter a value.') : undefined}
      />
      {!!onChange && <OnChange name={name}>{debouncedChange}</OnChange>}
    </Box>
  );
};

interface VariantOptionsSelectorProps {
  name: string;
  options: Option[];
  isInitialEntryManual?: boolean;
  shouldIncludeOtherOption?: boolean;
  onChange?: () => void;
  onSelectorTypeChange?: () => void;
}

const VariantOptionsSelector: FC<VariantOptionsSelectorProps> = (props) => {
  const {
    name,
    options,
    isInitialEntryManual = false,
    shouldIncludeOtherOption,
    onChange,
    onSelectorTypeChange,
  } = props;

  const [shouldUseDropdown, setShouldUseDropdown] = useState<boolean>(!isInitialEntryManual);
  const { allowCustomVariantOptions } = useListingFlowConfig();
  const { customItemDetailsInputFields } = useShopConfig();

  useEffect(() => {
    setShouldUseDropdown(!isInitialEntryManual);
  }, [isInitialEntryManual]);

  const handleToggleManualEntry = () => {
    if (onSelectorTypeChange) onSelectorTypeChange();
    setShouldUseDropdown(!shouldUseDropdown);
  };

  return (
    <>
      {!allowCustomVariantOptions || shouldUseDropdown ? (
        <VariantOptionsDropdown
          name={name}
          options={options}
          shouldIncludeOtherOption={shouldIncludeOtherOption}
          onChange={onChange}
        />
      ) : (
        <VariantOptionsTextInput name={name} onChange={onChange} />
      )}
      {allowCustomVariantOptions && !!onSelectorTypeChange && (
        <Box>
          <ManualEntryButton
            isManualEntryToggled={!shouldUseDropdown}
            toggleManualEntry={handleToggleManualEntry}
          />
        </Box>
      )}
      {!isEmpty(customItemDetailsInputFields) &&
        customItemDetailsInputFields.map((field: CustomItemDetailsInputField) => (
          <VariantOptionsTextInput
            key={field.key}
            name={field.key}
            label={field.label}
            placeholder={field.placeholder}
            type={field.type}
            isRequired={field.required}
          />
        ))}
    </>
  );
};

interface SizeSelectorProps {
  onChange?: () => void;
}

const SizeSelector: FC<SizeSelectorProps> = (props) => {
  const { onChange } = props;

  const shopConfig = useShopConfig();
  const form = useForm();
  const {
    sizeVariantOptionName,
    allSizeVariantOptionNames = [sizeVariantOptionName],
    variantOptions = [],
    customVariantDropdownOptions = [],
  } = shopConfig;
  const staticSizeConfig = getFilterOptions(shopConfig, FilterId.Size) as Option[];
  const staticSizeFormValues = staticSizeConfig?.map((sizeConfig) => sizeConfig?.key);

  const sizeInitialValue = form.getFieldState(sizeVariantOptionName)?.initial;
  const isSizeManuallyEntered =
    !!sizeInitialValue && !staticSizeFormValues?.includes(sizeInitialValue as string);

  const { allowCustomSizeOptions } = useListingFlowConfig();
  const [shouldUseDropdown, setShouldUseDropdown] = useState<boolean>(!isSizeManuallyEntered);

  useEffect(() => {
    setShouldUseDropdown(!isSizeManuallyEntered);
  }, [isSizeManuallyEntered]);

  const handleToggleManualEntry = () => {
    form.resetFieldState(sizeVariantOptionName);
    form.change(sizeVariantOptionName, undefined);
    setShouldUseDropdown(!shouldUseDropdown);
  };

  const { currentListing } = useOwnListingVersions();
  const { publicData } = currentListing.attributes;
  const { shopifyProduct } = publicData;

  const sizeOption = shopifyProduct?.options?.find((option: ProductOption) =>
    allSizeVariantOptionNames.includes(option.name.toLowerCase())
  );

  // This forces a standard size selection dropdown for brands that have
  // several size variant option names (e.g. size, waist, inseam, etc.)
  const customVariantSizeOption = customVariantDropdownOptions.some(
    (option: CustomVariantDropDownOption) => option.name === sizeVariantOptionName
  );

  const sizeVariantOption = variantOptions.find(
    (option: string) => option === sizeVariantOptionName
  );
  const shouldShowSizeSelector =
    (!!sizeOption || !!customVariantSizeOption || (!shopifyProduct && !!sizeVariantOption)) &&
    !!staticSizeConfig;

  if (!shouldShowSizeSelector) return null;

  return (
    <>
      {!allowCustomSizeOptions || shouldUseDropdown ? (
        <VariantOptionsDropdown
          name={sizeVariantOptionName}
          options={staticSizeConfig}
          shouldIncludeOtherOption
          onChange={onChange}
        />
      ) : (
        <VariantOptionsTextInput name={sizeVariantOptionName} onChange={onChange} />
      )}
      {allowCustomSizeOptions && (
        <Box>
          <ManualEntryButton
            isManualEntryToggled={!shouldUseDropdown}
            toggleManualEntry={handleToggleManualEntry}
          />
        </Box>
      )}
    </>
  );
};

interface ShopifyProductVariantOptionsProps {
  options: ProductOption[];
  onChange: () => void;
}

const ShopifyProductVariantOptions: FC<ShopifyProductVariantOptionsProps> = (props) => {
  const { options, onChange } = props;

  const form = useForm();

  return (
    <>
      {options.map((option) => {
        const fieldName = option.name.toLowerCase();
        const optionConfig = option.values?.map((value) => ({ key: value, label: value })) || [];
        const isOptionManuallyEntered =
          !!form.getFieldState(fieldName)?.initial &&
          !option.values?.includes(form.getFieldState(fieldName)?.initial as string);

        return (
          <VariantOptionsSelector
            key={fieldName}
            name={fieldName}
            options={optionConfig}
            isInitialEntryManual={isOptionManuallyEntered}
            onChange={onChange}
            onSelectorTypeChange={() => {
              form.resetFieldState(fieldName);
              form.change(fieldName, undefined);
            }}
          />
        );
      })}
    </>
  );
};

interface SkuSelectorForShopifyProductProps {
  shopifyProductId: string;
}

const SkuSelectorForShopifyProduct: FC<SkuSelectorForShopifyProductProps> = (props) => {
  const { shopifyProductId } = props;

  const { treetId } = useContext(AppContext);
  const { countryCode } = useBrandCountryConfig();
  const form = useForm();
  const { values } = useFormState();
  const {
    [ListingFlowFormField.ShopifyProductVariantIdBySku]: shopifyProductVariantIdBySkuFormValue,
  } = values;

  const { lazyQuery: lazyFetchVariantById, data: productVariantById } =
    useLazyApi(fetchVariantById);

  const { lazyQuery: lazyFetchVariantsByProductId, data: variantsByProductId } =
    useLazyApi(fetchVariantsByProductId);

  const [productVariantSkus, setProductVariantSkus] = useState<Option[]>([]);

  // Recursively fetch all pages so we can show all Shopify Product Variant IDs
  const fetchAllVariantIds = async (cursor = null) => {
    lazyFetchVariantsByProductId({ treetId, productId: shopifyProductId, cursor });
  };

  useEffect(() => {
    const newEdges = variantsByProductId?.product?.variants?.edges || [];
    const updatedResults = [...productVariantSkus, ...newEdges];
    const hasNextPage = variantsByProductId?.product?.variants?.pageInfo?.hasNextPage;
    const newCursor = variantsByProductId?.product?.variants?.edges?.slice(-1)[0]?.cursor;
    if (hasNextPage) {
      fetchAllVariantIds(newCursor);
    }
    setProductVariantSkus(updatedResults);
  }, [variantsByProductId]);

  useEffect(() => {
    // Fetch the first 30 variant ids and sku values for the product to seed the dropdown.
    if (shopifyProductId) {
      fetchAllVariantIds();
    }
  }, [shopifyProductId]);

  useEffect(() => {
    // Update the Shopify Product Variant when a new sku has been selected.
    if (productVariantById?.productVariant) {
      form.change(ListingFlowFormField.ShopifyProductVariant, productVariantById.productVariant);
    }
  }, [productVariantById]);

  const handleSkuOptionChange = () => {
    // Fetch info for selected product variant. Form will update in useEffect
    // when response is returned.
    if (shopifyProductVariantIdBySkuFormValue) {
      lazyFetchVariantById({
        treetId,
        countryCode,
        variantId: shopifyProductVariantIdBySkuFormValue,
      });
    }
  };

  const formatVariantsForDropdown = (variantIdsForProduct: any) => {
    if (!isEmpty(variantIdsForProduct)) {
      const shopifyProductVariantIdsToSkus = variantIdsForProduct
        .map((variant: { node: { id: string; sku: string } }) => {
          const { id, sku } = variant.node;
          if (sku && id) {
            return { key: id, label: sku } as Option;
          }

          return {};
        })
        .filter((option: any) => !!option.key);
      return uniqBy(shopifyProductVariantIdsToSkus, (option: Option) => option.key);
    }
    return [];
  };

  return (
    <>
      <VariantOptionsDropdown
        name={ListingFlowFormField.ShopifyProductVariantIdBySku}
        options={formatVariantsForDropdown(productVariantSkus) as Option[]}
        onChange={handleSkuOptionChange}
        isRequired={false}
      />
      <Box mt={1}>
        <TypographyWrapper variant="body2" typographyOverrides={{ style: { fontSize: '11px' } }}>
          Shown for Brand Direct sellers only. Optional, but recommended. This will help us identify
          the correct Shopify Variant based on SKU rather than the selected variant options for
          maximum accuracy.
        </TypographyWrapper>
      </Box>
    </>
  );
};

interface OptionsForShopifyProductProps {
  shopifyProductId?: string;
  shopifyProductOptions: ProductOption[];
}

const OptionsForShopifyProduct: FC<OptionsForShopifyProductProps> = (props) => {
  const { shopifyProductId, shopifyProductOptions } = props;

  const form = useForm();
  const {
    sizeVariantOptionName,
    allSizeVariantOptionNames = [sizeVariantOptionName],
    shouldUseStaticSizes,
    shopId,
    customVariantDropdownOptions = [],
  } = useShopConfig();
  const { categoriesConfig } = useShopConfigV2();
  const { isBrand } = useCurrentUserPermissions();
  const { countryCode } = useBrandCountryConfig();

  const categories = categoriesConfig?.categoriesCollection?.items || [];
  const options = filterDefaultShopifyOptions(shopifyProductOptions, shopId) as ProductOption[];

  const { lazyQuery: lazyFetchVariantByQuery, data: productVariantByQueryData } = useLazyApi(
    fetchShopifyVariantByQuery
  );

  useEffect(() => {
    const shopifyProductVariants = productVariantByQueryData?.productVariants?.edges?.map(
      (productVariantEdge: ShopifyProductVariantEdge) => productVariantEdge.node
    );

    if (!isEmpty(shopifyProductVariants)) {
      const { values } = form.getState();
      const optionsObj: { [optionName: string]: string } = {};
      options.forEach((option) => {
        optionsObj[option.name.toLowerCase()] = values[option.name.toLowerCase()];
      });
      const shopifyProductVariant = findMatchingProductVariant(
        shopifyProductVariants,
        optionsObj,
        sizeVariantOptionName
      );
      if (shopifyProductVariant) {
        form.change(ListingFlowFormField.ShopifyProductVariant, shopifyProductVariant);
      }
    }
  }, [productVariantByQueryData]);

  const handleVariantOptionChange = () => {
    const { values } = form.getState();
    const optionFields = options.map((option) => option.name.toLowerCase());
    const areAllOptionsFilled = optionFields.every(
      (field: string) => Object.keys(values).includes(field) && !!values[field]
    );

    if (areAllOptionsFilled && !values[ListingFlowFormField.ShopifyProductVariantIdBySku]) {
      lazyFetchVariantByQuery({
        queryString: getProductVariantQuery(
          optionFields,
          values,
          shopifyProductId,
          sizeVariantOptionName
        ),
        countryCode,
        subdomain: shopId,
      });
    }
  };

  const customVariantsToShow = customVariantDropdownOptions.filter(
    (option: CustomVariantDropDownOption) =>
      // If size is included, it's shown in the SizeSelector, so filter it out here
      option.showForShopifyProduct && !allSizeVariantOptionNames.includes(option.name.toLowerCase())
  );

  const onCustomVariantOptionChange = () => {
    handleCustomVariantOptionChange(form, customVariantsToShow);
  };

  const shouldShowCategorySelector = !isEmpty(categories);
  const sizeOption = options.find((option) =>
    allSizeVariantOptionNames.includes(option.name.toLowerCase())
  );
  const variantOptionsExcludingSize = options.filter((option) => option.name !== sizeOption?.name);

  return (
    <>
      {shouldShowCategorySelector && (
        <VariantOptionsSelector
          name={ListingFlowFormField.Category}
          options={categories}
          shouldIncludeOtherOption
        />
      )}
      {shouldUseStaticSizes && <SizeSelector onChange={handleVariantOptionChange} />}
      {customVariantsToShow.length > 0 &&
        customVariantsToShow.map((option: CustomVariantDropDownOption) => (
          <VariantOptionsDropdown
            key={option.key || option.name}
            name={option.name}
            options={option.values}
            description={option.description}
            onChange={onCustomVariantOptionChange}
          />
        ))}
      <ShopifyProductVariantOptions
        onChange={handleVariantOptionChange}
        options={shouldUseStaticSizes ? variantOptionsExcludingSize : options}
      />
      {isBrand && shopifyProductId && (
        <SkuSelectorForShopifyProduct shopifyProductId={shopifyProductId} />
      )}
    </>
  );
};

interface ManualVariantOptionsProps {
  options: string[];
}

const ManualVariantOptions: FC<ManualVariantOptionsProps> = (props) => {
  const { options } = props;
  return (
    <>
      {options.map((option) => (
        <VariantOptionsTextInput key={option} name={option} />
      ))}
    </>
  );
};

const OptionsForUserGeneratedTitle: FC = () => {
  const {
    sizeVariantOptionName,
    variantOptions = [],
    customVariantDropdownOptions = [],
  } = useShopConfig();
  const { categoriesConfig } = useShopConfigV2();
  const form = useForm();
  const categories = categoriesConfig?.categoriesCollection?.items || [];

  const sizeOption = variantOptions.find(
    (option: string) => option.toLowerCase() === sizeVariantOptionName
  );
  const variantOptionsExcludingSize = variantOptions.filter(
    (option: string) => option !== sizeOption
  );

  // Prevent from showing custom variant dropdown options that have no values
  const filteredCustomVariantDropdownOptions = customVariantDropdownOptions.filter(
    (option: CustomVariantDropDownOption) =>
      // If size is included, it's shown in the SizeSelector, so filter it out here
      !isEmpty(option.values) && option.name !== sizeVariantOptionName
  );

  const onCustomVariantOptionChange = () => {
    handleCustomVariantOptionChange(form, filteredCustomVariantDropdownOptions);
  };

  return (
    <>
      {!isEmpty(categories) && (
        <VariantOptionsSelector
          name={ListingFlowFormField.Category}
          options={categories}
          shouldIncludeOtherOption
        />
      )}
      <SizeSelector />
      <ManualVariantOptions options={variantOptionsExcludingSize} />
      {!isEmpty(filteredCustomVariantDropdownOptions) &&
        filteredCustomVariantDropdownOptions.map((option: CustomVariantDropDownOption) => (
          <VariantOptionsDropdown
            key={option.key || option.name}
            name={option.name}
            options={option.values}
            description={option.description}
            onChange={onCustomVariantOptionChange}
          />
        ))}
    </>
  );
};

const OptionsForListAnyBrand: FC = () => {
  const { categoriesConfig } = useShopConfigV2();
  const categories =
    categoriesConfig?.categoriesCollection?.items.filter(
      (item) => item.enableCategoryForListAnyBrandFlow
    ) || [];

  return (
    <>
      {!isEmpty(categories) && (
        <VariantOptionsSelector
          name={ListingFlowFormField.Category}
          options={categories}
          shouldIncludeOtherOption
        />
      )}
    </>
  );
};

const VariantOptions: FC = () => {
  const { variantOptions = [], shopId } = useShopConfig();
  const { categoriesConfig } = useShopConfigV2();
  const categories = categoriesConfig?.categoriesCollection?.items || [];
  const { currentListing } = useOwnListingVersions();
  const { shopifyProduct, findItemMethod } = currentListing.attributes?.publicData || {};

  const options = filterDefaultShopifyOptions(shopifyProduct?.options, shopId);
  const hasNoOptions =
    (!isEmpty(shopifyProduct) && isEmpty(options)) || variantOptions.length === 0;
  const hasNoCategories = isEmpty(categories);
  if (hasNoOptions && hasNoCategories) return null;

  const noShopifyProductOptions =
    findItemMethod === FindItemMethod.ListAnyBrand ? (
      <OptionsForListAnyBrand />
    ) : (
      <OptionsForUserGeneratedTitle />
    );
  const hasShopifyProductOptions = shopifyProduct?.options;

  return (
    <>
      <Heading>Item Details</Heading>
      <Box display="flex" flexDirection="column" maxWidth="400px">
        {hasShopifyProductOptions && (
          <OptionsForShopifyProduct
            shopifyProductId={shopifyProduct.id}
            shopifyProductOptions={shopifyProduct.options}
          />
        )}
        {!hasShopifyProductOptions && noShopifyProductOptions}
      </Box>
    </>
  );
};

export default VariantOptions;
